import { type ButtonHTMLAttributes, forwardRef } from 'react';
import { cn } from '../../utils/cn';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant = 'primary', size = 'md', fullWidth = false, disabled, style, ...props },
    ref
  ) => {
    const baseStyles =
      'inline-flex items-center justify-center rounded-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50';

    const variants = {
      primary: 'bg-primary text-white hover:bg-primary/90 focus-visible:ring-primary',
      secondary: 'bg-secondary text-white hover:bg-secondary/80 focus-visible:ring-secondary',
      outline:
        'border border-input bg-background text-foreground hover:bg-slate-100 hover:text-foreground focus-visible:ring-ring dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700 dark:hover:text-white',
      ghost:
        'text-foreground hover:bg-slate-100 hover:text-foreground focus-visible:ring-ring dark:text-slate-100 dark:hover:bg-slate-800 dark:hover:text-white',
      danger: 'bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive',
    };

    const sizes = {
      sm: 'text-sm',
      md: 'text-base',
      lg: 'text-lg',
    };

    const sizeStyles = {
      sm: {
        height: '2rem', // h-8 = 2rem = 32px
        paddingLeft: '0.75rem', // px-3 = 0.75rem = 12px
        paddingRight: '0.75rem',
      },
      md: {
        height: '2.5rem', // h-10 = 2.5rem = 40px
        paddingLeft: '1rem', // px-4 = 1rem = 16px
        paddingRight: '1rem',
      },
      lg: {
        height: '3rem', // h-12 = 3rem = 48px
        paddingLeft: '1.5rem', // px-6 = 1.5rem = 24px
        paddingRight: '1.5rem',
      },
    };

    return (
      <button
        ref={ref}
        className={cn(baseStyles, variants[variant], sizes[size], fullWidth && 'w-full', className)}
        style={{ ...sizeStyles[size], ...style }}
        disabled={disabled}
        {...props}
      />
    );
  }
);

Button.displayName = 'Button';
